import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import type { Deal, DealUpdate, Member } from '../api/types';
import { useDialog } from '../components/dialogContext';
import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { fmtDate, fmtDateTime, localDate } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { euro, FULL_SHARE, levelLabel, parseEuro, parsePercent, pct, previewAmounts } from './format';

const statusLabels: Record<Deal['status'], string> = { DRAFT: 'Entwurf', CLOSED: 'Abgeschlossen', CANCELED: 'Storniert' };
const statusClass: Record<Deal['status'], string> = { DRAFT: 'badge badge-muted', CLOSED: 'badge badge-ok', CANCELED: 'badge badge-muted' };

interface Person {
  id: string;
  name: string;
}

function uniqueMembers(items: Member[]): Person[] {
  const seen = new Map<string, string>();
  for (const m of items) if (m.status === 'ACTIVE' && !seen.has(m.user_id)) seen.set(m.user_id, `${m.first_name} ${m.last_name}`.trim());
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

// DealsPanel: Abschlüsse des Leads mit Provision und Verteilung auf die Vertriebsstruktur. Sätze kommen aus dem
// fertiggestellten Maklervertrag; die Verteilung aus dem Verteilungsplan des Projekts und der Struktur zum Abschlusstag.
// Mit dem Abschließen wird sie eingefroren – spätere Umstrukturierungen ändern sie nicht.
export function DealsPanel({ leadId }: { leadId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const deals = useQuery({ queryKey: projectKeys.deals(project.id, leadId), queryFn: ({ signal }) => papi.deals(leadId, signal) });
  const members = useQuery({ queryKey: projectKeys.members(project.id), queryFn: ({ signal }) => papi.members(signal), enabled: can('deal.manage') });
  const [creating, setCreating] = useState(false);
  const people = uniqueMembers(members.data?.items ?? []);
  return (
    <section className="card" aria-labelledby={`deals-${leadId}`}>
      <div className="section-head">
        <h2 id={`deals-${leadId}`}>Abschluss & Provision</h2>
        {can('deal.manage') && !creating && (
          <button type="button" className="btn btn-primary btn-small" onClick={() => setCreating(true)}>
            Abschluss erfassen
          </button>
        )}
      </div>
      {creating && <DealCreateForm leadId={leadId} people={people} onDone={() => setCreating(false)} />}
      {deals.isError && <ErrorMessage error={deals.error} onRetry={() => void deals.refetch()} />}
      {deals.data && deals.data.items.length === 0 && !creating && (
        <p className="muted">Noch kein Abschluss. Die Provision wird nach dem Verteilungsplan des Projekts auf die Vertriebsstruktur verteilt.</p>
      )}
      {deals.data?.items.map((d) => <DealCard key={d.id} deal={d} leadId={leadId} people={people} />)}
    </section>
  );
}

function DealCreateForm({ leadId, people, onDone }: { leadId: string; people: Person[]; onDone: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ closed_on: localDate(), price: '', closer: '', seller: '', buyer: '' });
  const create = useMutation({
    mutationFn: () =>
      papi.createDeal({
        lead_id: leadId,
        closed_on: form.closed_on,
        sale_price_cents: parseEuro(form.price) ?? 0,
        closer_user_id: form.closer || undefined,
        commission_seller_bp: form.seller ? (parsePercent(form.seller) ?? undefined) : undefined,
        commission_buyer_bp: form.buyer ? (parsePercent(form.buyer) ?? undefined) : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.deals(project.id, leadId) });
      onDone();
    },
  });
  const priceOk = parseEuro(form.price) !== null;
  const ratesOk = (!form.seller || parsePercent(form.seller) !== null) && (!form.buyer || parsePercent(form.buyer) !== null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (priceOk && ratesOk) create.mutate();
  };
  return (
    <form className="inline-form" onSubmit={submit} aria-label="Abschluss erfassen">
      <label className="field narrow">
        <span>Abschlusstag (Kaufvertrag)</span>
        <input type="date" value={form.closed_on} max={localDate()} onChange={(e) => setForm({ ...form, closed_on: e.target.value })} required />
      </label>
      <label className="field narrow">
        <span>Kaufpreis (€)</span>
        <input inputMode="decimal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="500.000" required />
      </label>
      <div className="field">
        <label htmlFor={`deal-closer-${leadId}`}>Abschließender Mitarbeiter</label>
        <select id={`deal-closer-${leadId}`} value={form.closer} onChange={(e) => setForm({ ...form, closer: e.target.value })}>
          <option value="">Makler des Leads (Standard)</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <label className="field narrow">
        <span>Provision Verkäufer (% inkl. USt.)</span>
        <input inputMode="decimal" value={form.seller} onChange={(e) => setForm({ ...form, seller: e.target.value })} placeholder="aus Maklervertrag" />
      </label>
      <label className="field narrow">
        <span>Provision Käufer (% inkl. USt.)</span>
        <input inputMode="decimal" value={form.buyer} onChange={(e) => setForm({ ...form, buyer: e.target.value })} placeholder="aus Maklervertrag" />
      </label>
      <button type="submit" className="btn btn-primary" disabled={create.isPending || !priceOk || !ratesOk}>
        Entwurf anlegen
      </button>
      <button type="button" className="btn btn-ghost" onClick={onDone}>
        Abbrechen
      </button>
      {create.isError && <MutationError error={create.error} />}
    </form>
  );
}

interface EditLine {
  userId: string;
  name: string;
  level: number | null;
  share: string;
}

function DealCard({ deal, leadId, people }: { deal: Deal; leadId: string; people: Person[] }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const [edit, setEdit] = useState<EditLine[] | null>(null);
  const [adding, setAdding] = useState('');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: projectKeys.deals(project.id, leadId) });
  const update = useMutation({
    mutationFn: (body: DealUpdate) => papi.updateDeal(deal.id, body),
    onSuccess: () => {
      setEdit(null);
      refresh();
    },
  });
  const close = useMutation({ mutationFn: () => papi.closeDeal(deal.id), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: (reason: string) => papi.cancelDeal(deal.id, reason), onSuccess: refresh });
  const editable = deal.status === 'DRAFT' && can('deal.manage');
  const shares = edit?.map((l) => parsePercent(l.share)) ?? [];
  const sum = shares.reduce<number>((a, b) => a + (b ?? 0), 0);
  const invalid = shares.some((s) => s === null) || sum > FULL_SHARE;
  const preview = previewAmounts(
    deal.commission_net_cents,
    shares.map((s) => s ?? 0),
  );
  const company = deal.splits.find((s) => s.recipient_type === 'COMPANY');
  const startEdit = () =>
    setEdit(
      deal.splits
        .filter((s) => s.recipient_type === 'USER' && s.user_id)
        .map((s) => ({ userId: s.user_id!, name: s.recipient_name, level: s.level ?? null, share: String(s.share_bp / 100).replace('.', ',') })),
    );
  const onClose = async () => {
    const ok = await dialog.confirm({
      title: 'Abschluss abschließen?',
      message:
        deal.splits_source === 'PLAN'
          ? 'Die Verteilung wird mit der Struktur zum Abschlusstag berechnet und danach eingefroren. Spätere Änderungen an der Struktur ändern sie nicht mehr.'
          : 'Die individuelle Verteilung wird eingefroren. Spätere Änderungen an der Struktur ändern sie nicht mehr.',
      confirmLabel: 'Abschließen',
    });
    if (ok) close.mutate();
  };
  const onCancel = async () => {
    const reason = await dialog.prompt({ title: 'Abschluss stornieren', label: 'Grund', confirmLabel: 'Stornieren', required: true });
    if (reason) cancel.mutate(reason);
  };
  const addable = people.filter((p) => !edit?.some((l) => l.userId === p.id));
  return (
    <article className="deal-card" aria-label={`Abschluss vom ${fmtDate(deal.closed_on)}`}>
      <h3>
        Abschluss vom {fmtDate(deal.closed_on)} <span className={statusClass[deal.status]}>{statusLabels[deal.status]}</span>
      </h3>
      <p>
        Kaufpreis <strong>{euro(deal.sale_price_cents)}</strong> · Provision {pct(deal.commission_seller_bp)} Verkäufer + {pct(deal.commission_buyer_bp)} Käufer
        (inkl. USt.) = {euro(deal.commission_gross_cents)} brutto · <strong>{euro(deal.commission_net_cents)} netto verteilbar</strong>
      </p>
      <p className="muted">
        {deal.closer_name ? `Abschließend: ${deal.closer_name}` : 'Ohne abschließenden Mitarbeiter'}
        {deal.document_id && ' · Sätze aus dem Maklervertrag'}
        {deal.chain.length > 1 && ` · Struktur zum Abschlusstag: ${deal.chain.map((m) => m.name).join(' → ')}`}
        {deal.status === 'DRAFT' && deal.splits_source === 'PLAN' && ' · Verteilung aus dem Plan (wird beim Abschließen mit der Struktur zum Abschlusstag berechnet)'}
        {deal.splits_source === 'MANUAL' && ' · individuelle Verteilung'}
      </p>
      <div className="table-wrap">
        <table className="table split-table">
          <thead>
            <tr>
              <th>Empfänger</th>
              <th>Rolle</th>
              <th>Anteil</th>
              <th>Betrag</th>
              {edit && <th aria-label="Aktionen" />}
            </tr>
          </thead>
          <tbody>
            {edit
              ? edit.map((l, i) => (
                  <tr key={l.userId}>
                    <td>{l.name}</td>
                    <td>{levelLabel(l.level, 'USER')}</td>
                    <td>
                      <input
                        aria-label={`Anteil ${l.name} (%)`}
                        inputMode="decimal"
                        value={l.share}
                        onChange={(e) => setEdit(edit.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))}
                      />{' '}
                      %
                    </td>
                    <td>{euro(preview.persons[i] ?? 0)}</td>
                    <td>
                      <button type="button" className="btn btn-small btn-ghost" onClick={() => setEdit(edit.filter((_, j) => j !== i))}>
                        Entfernen
                      </button>
                    </td>
                  </tr>
                ))
              : deal.splits
                  .filter((s) => s.recipient_type === 'USER')
                  .map((s) => (
                    <tr key={s.user_id}>
                      <td>
                        {s.recipient_name}
                        {s.title && <small className="muted block">{s.title}</small>}
                      </td>
                      <td>{levelLabel(s.level, s.recipient_type)}</td>
                      <td>{pct(s.share_bp)}</td>
                      <td>{euro(s.amount_cents)}</td>
                    </tr>
                  ))}
            <tr>
              <td>Firma</td>
              <td>{levelLabel(null, 'COMPANY')}</td>
              <td className="split-sum">{edit ? pct(Math.max(0, FULL_SHARE - sum)) : pct(company?.share_bp ?? 0)}</td>
              <td>{edit ? euro(Math.max(0, preview.company)) : euro(company?.amount_cents ?? 0)}</td>
              {edit && <td />}
            </tr>
          </tbody>
        </table>
      </div>
      {edit && (
        <div className="inline-form">
          <div className="field">
            <label htmlFor={`deal-add-${deal.id}`}>Person hinzufügen</label>
            <select id={`deal-add-${deal.id}`} value={adding} onChange={(e) => setAdding(e.target.value)}>
              <option value="">Bitte wählen</option>
              {addable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-small"
            disabled={!adding}
            onClick={() => {
              const p = people.find((x) => x.id === adding);
              if (p) setEdit([...edit, { userId: p.id, name: p.name, level: null, share: '0' }]);
              setAdding('');
            }}
          >
            Hinzufügen
          </button>
          <span className={invalid ? 'text-danger' : 'muted'} role="status">
            {sum > FULL_SHARE
              ? `Zusammen ${pct(sum)} – höchstens 100 % sind verteilbar.`
              : invalid
                ? 'Anteile zwischen 0 und 100 % angeben.'
                : `Personen ${pct(sum)} · Firma ${pct(FULL_SHARE - sum)}`}
          </span>
        </div>
      )}
      {deal.status === 'CANCELED' && (
        <p className="muted">
          Storniert am {fmtDateTime(deal.canceled_at)}
          {deal.cancel_reason && `: ${deal.cancel_reason}`}
        </p>
      )}
      {deal.status === 'CLOSED' && (
        <p className="muted">
          Abgeschlossen am {fmtDateTime(deal.closed_at)}
          {deal.closed_by_name && ` von ${deal.closed_by_name}`} – die Verteilung ist eingefroren.
        </p>
      )}
      {can('deal.manage') && deal.status !== 'CANCELED' && (
        <div className="form-actions">
          {editable && !edit && (
            <>
              <button type="button" className="btn btn-primary" disabled={close.isPending} onClick={() => void onClose()}>
                Abschließen
              </button>
              <button type="button" className="btn" onClick={startEdit}>
                Verteilung anpassen
              </button>
              {deal.splits_source === 'MANUAL' && (
                <button type="button" className="btn" disabled={update.isPending} onClick={() => update.mutate({ recalculate: true })}>
                  Aus Plan und Struktur berechnen
                </button>
              )}
            </>
          )}
          {edit && (
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={invalid || update.isPending}
                onClick={() => update.mutate({ splits: edit.map((l) => ({ user_id: l.userId, share_bp: parsePercent(l.share) ?? 0 })) })}
              >
                Verteilung speichern
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setEdit(null)}>
                Verwerfen
              </button>
            </>
          )}
          {!edit && (
            <button type="button" className="btn btn-ghost" disabled={cancel.isPending} onClick={() => void onCancel()}>
              Stornieren
            </button>
          )}
        </div>
      )}
      {(update.isError || close.isError || cancel.isError) && <MutationError error={update.error ?? close.error ?? cancel.error} />}
    </article>
  );
}
